import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Eye, EyeOff, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api-client";

// The post-call webhook of one agent or assistant: where Chawowa posts
// call.ended and call.analyzed for its calls (backend: agent_webhooks.rs).

type Delivery = {
  id: string;
  event: string;
  status: string;
  response_code: number | null;
  error: string | null;
  attempts: number;
  created_at: string;
};

type Webhook = {
  endpoint_id?: string;
  url: string;
  enabled: boolean;
  secret: string | null;
  events: string[];
  recent_deliveries: Delivery[];
};

function errorText(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { error?: { message?: string; details?: Record<string, unknown> }; detail?: string }
    | undefined;
  const first = data?.error?.details && Object.values(data.error.details)[0];
  if (Array.isArray(first) && typeof first[0] === "string") return first[0];
  return data?.error?.message ?? data?.detail ?? "Something went wrong.";
}

const STATUS_STYLE: Record<string, string> = {
  sent: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  failed: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  dlq: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400",
};

export default function PostCallWebhookCard({
  owner,
  id,
  canEdit = true,
}: {
  owner: "agents" | "assistants";
  id: string;
  canEdit?: boolean;
}) {
  const qc = useQueryClient();
  const key = ["post-call-webhook", owner, id] as const;
  const base = `/${owner}/${id}/webhook/`;
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<Webhook>(base)).data,
    // Deliveries land a few seconds after a call or a test; keep the list fresh.
    refetchInterval: 15_000,
  });
  const hook = q.data;

  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (hook) {
      setUrl(hook.url);
      setEnabled(hook.url ? hook.enabled : true);
    }
  }, [hook?.url, hook?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = (data: Webhook) => qc.setQueryData(key, data);
  const save = useMutation({
    mutationFn: async () => (await api.put<Webhook>(base, { url: url.trim(), enabled })).data,
    onSuccess: (data) => {
      refresh(data);
      toast.success(data.url ? "Webhook saved." : "Webhook removed.");
    },
    onError: (e) => toast.error(errorText(e)),
  });
  const rotate = useMutation({
    mutationFn: async () => (await api.post<Webhook>(`${base}rotate-secret/`)).data,
    onSuccess: (data) => {
      refresh(data);
      setShowSecret(true);
      toast.success("New secret made. Update it in the receiving app.");
    },
    onError: (e) => toast.error(errorText(e)),
  });
  const test = useMutation({
    mutationFn: async () => (await api.post(`${base}test/`)).data,
    onSuccess: () => {
      toast.success("Test event sent. Its result appears below in a few seconds.");
      setTimeout(() => qc.invalidateQueries({ queryKey: key }), 4000);
    },
    onError: (e) => toast.error(errorText(e)),
  });

  const saved = !!hook?.url;
  const dirty = !!hook && (url.trim() !== hook.url || (saved && enabled !== hook.enabled));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Post-call webhook</CardTitle>
        <CardDescription>
          After each call, Chawowa posts the result to this URL: <code>call.ended</code> when the call is over
          (status, transcript, recording, the variables it was sent with), then <code>call.analyzed</code> with the
          summary and structured outputs. Failed posts are retried for about an hour.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor={`hook-url-${id}`}>URL</Label>
          <div className="flex gap-2">
            <Input
              id={`hook-url-${id}`}
              value={url}
              disabled={!canEdit}
              placeholder="https://your-app.example.com/hooks/chawowa"
              onChange={(e) => setUrl(e.target.value)}
            />
            {canEdit && (
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {url.trim() || !saved ? "Save" : "Remove"}
              </Button>
            )}
          </div>
          {saved && (
            <label className="flex items-center gap-2 pt-1 text-sm">
              <Switch checked={enabled} disabled={!canEdit} onCheckedChange={setEnabled} />
              Send events {enabled ? "" : "(paused)"}
            </label>
          )}
        </div>

        {saved && hook?.secret && (
          <div className="space-y-1.5">
            <Label>Signing secret</Label>
            <div className="flex gap-2">
              <Input readOnly value={showSecret ? hook.secret : "•".repeat(32)} className="font-mono text-xs" />
              <Button variant="outline" size="icon" aria-label="Show secret" onClick={() => setShowSecret((s) => !s)}>
                {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                aria-label="Copy secret"
                onClick={() => {
                  navigator.clipboard.writeText(hook.secret ?? "");
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
              {canEdit && (
                <Button variant="outline" disabled={rotate.isPending} onClick={() => rotate.mutate()}>
                  <RefreshCw className="size-4" />
                  New secret
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Each post carries <code>X-Chawowa-Timestamp</code> and <code>X-Chawowa-Signature: sha256=…</code>, the
              HMAC-SHA256 of <code>{"{timestamp}.{body}"}</code> with this secret. Check it before trusting a post.
            </p>
          </div>
        )}

        {saved && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Recent deliveries</Label>
              {canEdit && (
                <Button variant="outline" size="sm" disabled={test.isPending} onClick={() => test.mutate()}>
                  <Send className="size-4" />
                  Send test event
                </Button>
              )}
            </div>
            {(hook?.recent_deliveries ?? []).length === 0 ? (
              <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                Nothing sent yet. Send a test event, or place a call.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-3 py-2 font-medium">Event</th>
                      <th className="px-3 py-2 font-medium">Result</th>
                      <th className="px-3 py-2 font-medium">Tries</th>
                      <th className="px-3 py-2 font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(hook?.recent_deliveries ?? []).map((d) => (
                      <tr key={d.id} className="border-b last:border-0 align-top">
                        <td className="px-3 py-2 font-mono text-xs">{d.event}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`rounded px-2 py-0.5 font-medium ${STATUS_STYLE[d.status] ?? "bg-muted"}`}>
                            {d.status === "dlq" ? "gave up" : d.status}
                          </span>
                          {d.response_code != null && <span className="ml-2">HTTP {d.response_code}</span>}
                          {d.error && <div className="mt-1 text-muted-foreground">{d.error}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">{d.attempts}</td>
                        <td className="px-3 py-2 text-xs">{new Date(d.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
